// app/routes/signup.tsx
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useAuth0Login } from "../hooks/useAuth0Login";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { Spinner } from "~/components/ui/spinner";
import { useEffect, useState } from "react";
import { consumeJustLoggedOutFlag } from "~/lib/auth-recovery";

export const Route = createFileRoute("/signup")({
  component: SignUp,
});

function SignUp() {
  const { user, isLoading: isUserLoading, routing } = useAuthenticatedUser();
  const { isAuthenticated, isLoading: isAuthLoading, login } = useAuth0Login();
  const [hasRedirected, setHasRedirected] = useState(false);
  const [justLoggedOut, setJustLoggedOut] = useState(() => consumeJustLoggedOutFlag());

  const isLoading = isAuthLoading || isUserLoading;
  const isAlreadyAuthenticated = !isLoading && isAuthenticated && !!user;

  // Auto-redirect to Auth0 signup
  // Skip if we just completed a logout — the user wants to be logged out,
  // not immediately funneled back to Auth0.
  useEffect(() => {
    if (isAlreadyAuthenticated || justLoggedOut) return;
    if (!isAuthLoading && !hasRedirected) {
      setHasRedirected(true);
      login("signup");
    }
  }, [isAlreadyAuthenticated, justLoggedOut, isAuthLoading, hasRedirected, login]);

  // If authenticated with user, redirect to dashboard (tenant-aware)
  if (isAlreadyAuthenticated) {
    return <Navigate to={(routing?.homePath ?? "/dashboard") as any} replace />;
  }

  // After a normal logout, show a logged-out screen with a button to log in again
  if (justLoggedOut) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 p-8">
        <h1 className="text-2xl font-bold text-black">You have been logged out</h1>
        <p className="text-black text-center max-w-md">
          You have been successfully logged out of your account.
        </p>
        <button
          type="button"
          onClick={() => {
            setJustLoggedOut(false);
            login("login");
          }}
          className="inline-flex items-center justify-center rounded-md bg-brand-green-primary px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-green-primary/90 transition-colors"
        >
          Log in again
        </button>
      </div>
    );
  }

  // Show loading spinner
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Spinner className="size-8 text-brand-green-primary" />
    </div>
  );
}
